import React, { useState } from "react";
import { BookingButtonProps } from "./BookingButtonProps";
import { LabelsProvider } from "./LabelsContext/LabelsContext";
import { BookingProvider } from "./BookingContext/BookingContext";
import { BookingModalDecision } from "./BookingModalDecision";
import { FeatureFlagsProvider } from "../../Context/FeatureFlagsContext";
import { Button, getBookingButtonClass } from "../Button/Button";
import { GtmService } from "../Booking/GtmService";

interface ExtendedBookingButtonProps extends BookingButtonProps {
  awaitTime?: string;
  labelCssClass?: string;
  descriptionCssClass?: string;
  ignorePersistedData?: boolean;
  variant?: "hero" | "highlight" | "doctor-offered-treatments";
}

export const BookingButton: React.FC<ExtendedBookingButtonProps> = ({
  bookingButton,
  cssClass,
  labelCssClass,
  descriptionCssClass,
  waitlist,
  awaitTime,
  ignorePersistedData,
  variant,
}) => {
  const [isModalOpened, setIsModalOpened] = useState(false);

  const closeModal = () => setIsModalOpened(false);
  const openModal = () => {
    GtmService.bookingCtaClicked(variant, bookingButton.label);
    setIsModalOpened(true);
  };

  return (
    <>
      {bookingButton.targetUrl ? (
        variant === "hero" ? (
          <a
            className={getBookingButtonClass({
              size: "large",
              variant: "primary",
              className: cssClass,
            })}
            href={bookingButton.targetUrl}
            onClick={() =>
              GtmService.bookingCtaClicked(
                variant,
                bookingButton.label,
                bookingButton.targetUrl
              )
            }
          >
            <span className={labelCssClass}>{bookingButton.label}</span>
          </a>
        ) : (
          <a
            className={cssClass}
            href={bookingButton.targetUrl}
            onClick={() =>
              GtmService.bookingCtaClicked(
                variant,
                bookingButton.label,
                bookingButton.targetUrl
              )
            }
          >
            <span className={labelCssClass}>{bookingButton.label}</span>
          </a>
        )
      ) : (
        <FeatureFlagsProvider
          sdkKey={bookingButton.optimizelyExperimentation?.sdkKey}
        >
          <LabelsProvider bookingLabels={bookingButton.bookingLabels}>
            <BookingProvider
              dipsAutoQueryMaxRepeats={bookingButton.dipsAutoQueryMaxRepeats}
              requireParentSsn={bookingButton.requireParentSsn}
              filterValues={bookingButton.filterValues}
              newBookingFilterValues={bookingButton.newBookingFilterValues}
              awaitTime={awaitTime}
              waitlist={waitlist}
              blockedSsnErrorMessage={bookingButton.blockedSsnErrorMessage}
              blockedSsnList={bookingButton.blockedSsnList}
              optimizelyExperimentation={
                bookingButton.optimizelyExperimentation
              }
              trackAbEvent={bookingButton.trackAbEvent}
              bookingSearchSetup={bookingButton.bookingSearchSetup}
            >
              {variant === "hero" ? (
                <Button
                  label={bookingButton.label}
                  description={bookingButton.description}
                  onClick={openModal}
                  disabled={!!bookingButton.targetUrl}
                  labelClassName={labelCssClass}
                  descriptionClassName={descriptionCssClass}
                  size="large"
                  variant="primary"
                />
              ) : (
                <button className={cssClass} onClick={openModal}>
                  <div className={labelCssClass}>{bookingButton.label}</div>
                  {!!bookingButton.description && (
                    <div className={descriptionCssClass}>
                      {bookingButton.description}
                    </div>
                  )}
                </button>
              )}
              <BookingModalDecision
                bookingButton={bookingButton}
                closeModal={closeModal}
                isModalOpened={isModalOpened}
                ignorePersistedData={ignorePersistedData}
              />
            </BookingProvider>
          </LabelsProvider>
        </FeatureFlagsProvider>
      )}
    </>
  );
};
